import { type ThemeOptions, alpha, createTheme } from '@mui/material/styles'

// Professional Government Color Palette - Enhanced
const governmentColors = {
  // Primary - Sophisticated Deep Blue
  primary: {
    main: '#1E3A5F', // Rich government blue
    light: '#2E5185',
    dark: '#0E2545',
    contrastText: '#FFFFFF',
  },
  // Secondary - Elegant Steel
  secondary: {
    main: '#64748B', // Modern steel blue
    light: '#94A3B8',
    dark: '#475569',
    contrastText: '#FFFFFF',
  },
  // Error - Refined Alert Red
  error: {
    main: '#DC2626', // Clear error red
    light: '#EF4444',
    dark: '#B91C1C',
    contrastText: '#FFFFFF',
  },
  // Success - Professional Achievement Green
  success: {
    main: '#059669', // Professional success green
    light: '#10B981',
    dark: '#047857',
    contrastText: '#FFFFFF',
  },
  // Warning - Attention Amber
  warning: {
    main: '#F59E0B', // Clear warning amber
    light: '#FBBF24',
    dark: '#D97706',
    contrastText: '#000000',
  },
  // Info - Information Blue
  info: {
    main: '#3B82F6', // Clear info blue
    light: '#60A5FA',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },
  // Enhanced Neutral Scale
  neutral: {
    50: '#FAFBFC',
    100: '#F5F6F8',
    200: '#EBEEF2',
    300: '#DDE1E7',
    400: '#C4CAD4',
    500: '#8892A2',
    600: '#5C6A7D',
    700: '#3E4B5F',
    800: '#283242',
    900: '#161E2E',
    950: '#0B1221',
  },
}

// Professional Typography Configuration
const typographyConfig = {
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '2.5rem',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h2: {
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
  },
  h3: {
    fontSize: '1.75rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.4,
  },
  h4: {
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '0em',
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '0em',
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '0em',
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    letterSpacing: '0em',
    lineHeight: 1.6,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '0.01em',
    lineHeight: 1.6,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    letterSpacing: '0em',
    lineHeight: 1.6,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    letterSpacing: '0.01em',
    lineHeight: 1.6,
  },
  button: {
    textTransform: 'none' as const,
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    letterSpacing: '0.03em',
    lineHeight: 1.5,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    lineHeight: 1.5,
  },
}

// Common theme options for professional appearance
const commonThemeOptions: ThemeOptions = {
  typography: typographyConfig,
  shape: {
    borderRadius: 6,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 6px -1px rgba(0, 0, 0, 0.05)',
    '0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
    '0px 20px 25px -5px rgba(0, 0, 0, 0.1)',
    '0px 25px 30px -5px rgba(0, 0, 0, 0.1)',
    '0px 30px 35px -5px rgba(0, 0, 0, 0.1)',
    '0px 35px 40px -5px rgba(0, 0, 0, 0.1)',
    '0px 40px 45px -5px rgba(0, 0, 0, 0.1)',
    '0px 45px 50px -5px rgba(0, 0, 0, 0.1)',
    '0px 50px 55px -5px rgba(0, 0, 0, 0.1)',
    '0px 55px 60px -5px rgba(0, 0, 0, 0.1)',
    '0px 60px 65px -5px rgba(0, 0, 0, 0.1)',
    '0px 65px 70px -5px rgba(0, 0, 0, 0.1)',
    '0px 70px 75px -5px rgba(0, 0, 0, 0.1)',
    '0px 75px 80px -5px rgba(0, 0, 0, 0.1)',
    '0px 80px 85px -5px rgba(0, 0, 0, 0.1)',
    '0px 85px 90px -5px rgba(0, 0, 0, 0.1)',
    '0px 90px 95px -5px rgba(0, 0, 0, 0.1)',
    '0px 95px 100px -5px rgba(0, 0, 0, 0.1)',
    '0px 100px 105px -5px rgba(0, 0, 0, 0.1)',
    '0px 105px 110px -5px rgba(0, 0, 0, 0.1)',
    '0px 110px 115px -5px rgba(0, 0, 0, 0.1)',
    '0px 115px 120px -5px rgba(0, 0, 0, 0.1)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: '#9CA3AF #F3F4F6',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            backgroundColor: '#9CA3AF',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: '#F3F4F6',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          letterSpacing: '0.02em',
          transition: 'all 0.2s ease-in-out',
          boxShadow: 'none',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            opacity: 0.6,
          },
        },
        sizeLarge: {
          padding: '14px 32px',
          fontSize: '0.95rem',
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: '0.8125rem',
        },
        contained: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${governmentColors.primary.main} 0%, ${governmentColors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${governmentColors.primary.light} 0%, ${governmentColors.primary.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(governmentColors.primary.main, 0.04),
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            fontSize: '0.95rem',
            transition: 'all 0.2s ease-in-out',
            backgroundColor: 'transparent',
            '& fieldset': {
              borderWidth: '1.5px',
              transition: 'all 0.2s ease-in-out',
            },
            '&:hover': {
              '& fieldset': {
                borderColor: governmentColors.primary.main,
                borderWidth: '2px',
              },
            },
            '&.Mui-focused': {
              '& fieldset': {
                borderWidth: '2px',
              },
              boxShadow: `0 0 0 3px ${alpha(governmentColors.primary.main, 0.1)}`,
            },
            '&.Mui-error': {
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px ${alpha(governmentColors.error.main, 0.1)}`,
              },
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.9rem',
            fontWeight: 500,
            '&.Mui-focused': {
              fontWeight: 600,
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '0.8rem',
            marginTop: '6px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          border: '1px solid',
          borderColor: governmentColors.neutral[200],
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.05)',
        },
        elevation3: {
          boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid',
          borderBottomColor: governmentColors.neutral[200],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '4px 0px 6px -1px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: governmentColors.neutral[50],
          borderBottom: `2px solid ${governmentColors.neutral[200]}`,
        },
        root: {
          borderBottom: `1px solid ${governmentColors.neutral[200]}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          backdropFilter: 'blur(8px)',
          border: '1px solid',
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: 'translateY(-10px)',
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        },
        standardError: {
          backgroundColor: alpha(governmentColors.error.main, 0.08),
          color: governmentColors.error.dark,
          '& .MuiAlert-icon': {
            color: governmentColors.error.main,
          },
        },
        standardWarning: {
          backgroundColor: alpha(governmentColors.warning.main, 0.08),
          color: governmentColors.warning.dark,
          '& .MuiAlert-icon': {
            color: governmentColors.warning.main,
          },
        },
        standardInfo: {
          backgroundColor: alpha(governmentColors.info.main, 0.08),
          color: governmentColors.info.dark,
          '& .MuiAlert-icon': {
            color: governmentColors.info.main,
          },
        },
        standardSuccess: {
          backgroundColor: alpha(governmentColors.success.main, 0.08),
          color: governmentColors.success.dark,
          '& .MuiAlert-icon': {
            color: governmentColors.success.main,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: governmentColors.neutral[900],
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 12px',
          borderRadius: 4,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: governmentColors.neutral[200],
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
          backgroundColor: governmentColors.neutral[200],
          overflow: 'hidden',
        },
        bar: {
          borderRadius: 2,
          background: `linear-gradient(90deg, ${governmentColors.primary.main} 0%, ${governmentColors.primary.light} 50%, ${governmentColors.primary.main} 100%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        },
      },
    },
  },
}

// Light theme - Professional Government Standard
export const lightTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'light',
    ...governmentColors,
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: governmentColors.neutral[900],
      secondary: governmentColors.neutral[600],
      disabled: governmentColors.neutral[400],
    },
    divider: governmentColors.neutral[200],
    action: {
      hover: alpha(governmentColors.primary.main, 0.04),
      selected: alpha(governmentColors.primary.main, 0.08),
      disabled: governmentColors.neutral[300],
      disabledBackground: governmentColors.neutral[100],
    },
    grey: governmentColors.neutral,
  },
})

// Dark theme - Professional Government Standard
export const darkTheme = createTheme({
  ...commonThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: '#3B82F6', // Brighter blue for dark mode
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#94A3B8',
      light: '#CBD5E1',
      dark: '#64748B',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#000000',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0B1221',
      paper: '#161E2E',
    },
    text: {
      primary: '#F1F5F9',
      secondary: '#CBD5E1',
      disabled: '#64748B',
    },
    divider: alpha('#94A3B8', 0.12),
    action: {
      hover: alpha('#3B82F6', 0.08),
      selected: alpha('#3B82F6', 0.16),
      disabled: '#475569',
      disabledBackground: '#334155',
    },
    grey: {
      50: '#FAFBFC',
      100: '#F5F6F8',
      200: '#EBEEF2',
      300: '#DDE1E7',
      400: '#C4CAD4',
      500: '#8892A2',
      600: '#5C6A7D',
      700: '#3E4B5F',
      800: '#283242',
      900: '#161E2E',
    },
  },
  components: {
    ...commonThemeOptions.components,
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          border: '1px solid',
          borderColor: alpha('#94A3B8', 0.1),
          backgroundImage: 'none',
          backgroundColor: '#161E2E',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
            transform: 'translateY(-2px)',
            borderColor: alpha('#94A3B8', 0.2),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#1E293B',
          borderBottom: `2px solid ${alpha('#94A3B8', 0.12)}`,
        },
        root: {
          borderBottom: `1px solid ${alpha('#94A3B8', 0.12)}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
          borderBottom: '1px solid',
          borderBottomColor: alpha('#94A3B8', 0.12),
          backgroundColor: '#1E293B',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#334155',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
          backgroundColor: alpha('#94A3B8', 0.12),
          overflow: 'hidden',
        },
        bar: {
          borderRadius: 2,
          background: `linear-gradient(90deg, ${governmentColors.primary.light} 0%, ${governmentColors.primary.main} 50%, ${governmentColors.primary.light} 100%)`,
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s ease-in-out infinite',
          '@keyframes shimmer': {
            '0%': { backgroundPosition: '200% 0' },
            '100%': { backgroundPosition: '-200% 0' },
          },
        },
      },
    },
  },
})

// Export the default theme
export default lightTheme
